import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Minimize2 } from 'lucide-react';
import { useChat } from '../../context/ChatContext';
import { ChatSidebar } from './ChatSidebar';
import { ChatWindow } from './ChatWindow';
import { supabase } from '../../lib/supabase';
import { ChatUser } from '../../hooks/useRealtimeChat';

export default function ChatWidget() {
    const location = useLocation();
    const {
        chats,
        activeChatId,
        setActiveChatId,
        markChatAsRead,
        loading: chatsLoading
    } = useChat();

    const [isOpen, setIsOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState<ChatUser | null>(null);

    // Get current user info - We should really move this to a global AuthContext eventually
    useEffect(() => {
        const fetchUser = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                const { data: profile } = await supabase
                    .from('profiles')
                    .select('email')
                    .eq('id', session.user.id)
                    .single();

                setCurrentUser({
                    id: session.user.id,
                    name: profile?.email || session.user.email || 'User',
                    avatar_url: undefined
                });
            }
        };
        fetchUser();
    }, []);

    const activeChat = chats.find(c => c.id === activeChatId);

    const getOtherParticipantName = (chat: any) => {
        if (!currentUser) return 'User';
        const other = chat.participants?.find((p: any) => p.user_id !== currentUser.id);
        return other?.profile?.email || 'User';
    };

    // Auto-mark as read when open
    useEffect(() => {
        if (activeChatId && isOpen) {
            markChatAsRead(activeChatId);
        }
    }, [activeChatId, isOpen, markChatAsRead]);

    if (chatsLoading || location.pathname === '/weddings/messages') return null;

    return (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex flex-col items-end print:hidden">
            {/* Chat Window */}
            {isOpen && currentUser && (
                <div className="mb-4 w-[calc(100vw-3rem)] sm:w-96 h-[500px] max-h-[80vh] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col transition-all origin-bottom-right animate-in fade-in zoom-in-95 duration-200">

                    {/* Header */}
                    <div className="p-3 bg-rose-600 text-white flex items-center justify-between shrink-0">
                        <div className="flex items-center gap-2">
                            {activeChatId && (
                                <button
                                    onClick={() => setActiveChatId(null)}
                                    className="hover:bg-white/20 p-1 rounded -ml-1"
                                >
                                    <span className="text-xs">&larr; Back</span>
                                </button>
                            )}
                            <h3 className="font-semibold text-sm truncate max-w-[150px]">
                                {activeChatId && activeChat
                                    ? getOtherParticipantName(activeChat)
                                    : 'Messages'}
                            </h3>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded">
                                <Minimize2 className="h-4 w-4" />
                            </button>
                            <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-hidden flex flex-col relative">
                        {activeChatId ? (
                            <div className="absolute inset-0 z-10 bg-white dark:bg-gray-900">
                                <ChatWindow
                                    chatId={activeChatId}
                                    currentUser={currentUser}
                                    otherParticipantName={getOtherParticipantName(activeChat)}
                                />
                            </div>
                        ) : (
                            <ChatSidebar
                                chats={chats}
                                activeChatId={activeChatId}
                                onSelectChat={setActiveChatId}
                                currentUserId={currentUser.id}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Toggle Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="h-14 w-14 rounded-full bg-rose-600 hover:bg-rose-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
            >
                {isOpen ? <X className="h-7 w-7" /> : <MessageCircle className="h-7 w-7" />}
                {/* Unread badge could go here */}
            </button>
        </div>
    );
}
